import {view} from '@sail/ui/deprecated';
import {useCallback, useEffect, useState} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {defineMessages} from 'react-intl';
import {
  RequestACallConfirmation,
  RequestACallForm,
} from '../../components/Forms/RequestACall';
import {EligibleChannelsEndpointResponseChannelRequestACall} from '../../../generated/types';
import {getWaitTime} from '../../components/ChannelSelector/getDisplayChannels';
import {useEligibleChannels} from '../../components/ChannelSelector';
import {useProfile} from '../../Providers/ProfileProvider';
import {useNewDesignsEnabled} from '../../Screens/ChatScreen/useNewDesignsEnabled';

const DEFAULT_TOPIC = 'other';

type Props = {
  conversation: Conversation;
  onCancel: () => void;
};

export const messages = defineMessages({
  requestACallExplanationMessage: {
    id: 'support_conversations_client.supportWidget.rac.requestACallExplanaitonMessage',
    description:
      'Instructions shown to the user in the conversation log after they successfully submit a RAC request',
    defaultMessage:
      'We’re on it. You should receive a call at the number listed in the card above in the next 5 minutes. The agent will provide you with an identification code, and then ask you for your code shown above. Keep this chat open until you receive the call. ',
  },
});

export const RequestACallModal = ({conversation, onCancel}: Props) => {
  const newDesignsEnabled = useNewDesignsEnabled();
  const {profile} = useProfile();
  const {visitor} = profile;
  const [showConfirmation, setShowConfirmation] = useState<boolean>(
    conversation.context.channel === 'request_a_call',
  );
  const {eligibleChannels} = useEligibleChannels();
  const [channel, setChannel] = useState<
    EligibleChannelsEndpointResponseChannelRequestACall | undefined
  >();
  useEffect(() => {
    setChannel(
      eligibleChannels?.find((c) => {
        return c.type === 'request_a_call';
      }) as EligibleChannelsEndpointResponseChannelRequestACall | undefined,
    );
  }, [eligibleChannels]);

  const [name, setName] = useState<string>(visitor.displayName ?? '');
  const [phoneNumber, setPhoneNumber] = useState<string>(
    visitor.phoneNumber ?? '',
  );

  const handleSuccess = useCallback(
    ({
      name: submittedName,
      phoneNumber: submittedPhoneNumber,
    }: {
      name: string;
      phoneNumber: string;
    }) => {
      conversation.setContextValues({
        channel: 'request_a_call',
        requestACallDetails: {
          userName: submittedName,
          userPhoneNumber: submittedPhoneNumber,
        },
      });

      if (newDesignsEnabled) {
        conversation.log.appendMetaEvent({
          id: 'rac-explanation',
          message: messages.requestACallExplanationMessage,
          style: 'body',
        });
      }

      setShowConfirmation(true);
      setName(submittedName);
      setPhoneNumber(submittedPhoneNumber);
    },
    [conversation, newDesignsEnabled],
  );

  if (showConfirmation) {
    if (newDesignsEnabled) {
      // RAC confirmation appears as a topbar when new designs are enabled
      return null;
    }

    return (
      <RequestACallConfirmation
        name={name ?? visitor.displayName ?? ''}
        phoneNumber={phoneNumber ?? visitor.phoneNumber ?? ''}
        waitTime={channel ? getWaitTime(channel, false) : null}
      />
    );
  }

  return (
    <view.div data-testid="request-a-call-form-modal">
      <RequestACallForm
        topic={conversation.context.topic || DEFAULT_TOPIC}
        conversationId={conversation.id}
        initialMessage={
          conversation.context.latestQuestion as string | undefined
        }
        onSuccess={handleSuccess}
        onCancel={onCancel}
      />
    </view.div>
  );
};
